import * as Sentry from '@sentry/react';
import { db } from '../db';
import type { NutritionMyWorkItem } from '../db';
import { nutritionSessionService } from './nutritionSessionService';

/**
 * Nutrition My-Sessions worklist cache.
 *
 * Mirrors operatorSpraySessionCacheService's refresh/read pair but with
 * simpler semantics: the worklist is a flat snapshot (no optimistic
 * write-through, no offline queue pins) — each refresh is a clean
 * clear + bulkPut. On a network failure the cache is left intact so
 * the operator can still read their last-known worklist.
 *
 * Tables:
 *   nutritionMySessions  — introduced in Dexie v17.
 */

/**
 * Fetch GET /nutrition/sessions/my and replace the local cache atomically.
 *
 * On a network failure (or any thrown error): leave the cache untouched
 * and return { hasLinkedOperator: false } without rethrowing.
 */
export async function refreshMyNutritionSessions(): Promise<{ hasLinkedOperator: boolean }> {
  return Sentry.startSpan(
    { name: 'nutrition.cache.refreshMyNutritionSessions', op: 'cache.sync' },
    async () => {
      let items: NutritionMyWorkItem[];
      let hasLinkedOperator: boolean;
      try {
        const data = await nutritionSessionService.getMySessions();
        items = data.items;
        hasLinkedOperator = data.hasLinkedOperator;
      } catch (_err) {
        // Offline or transient network error — leave the cache untouched.
        return { hasLinkedOperator: false };
      }

      await db.transaction('rw', db.nutritionMySessions, async () => {
        await db.nutritionMySessions.clear();
        await db.nutritionMySessions.bulkPut(items);
      });

      return { hasLinkedOperator };
    },
  );
}

/**
 * Return every cached nutrition My-Sessions worklist row.
 * Returns an empty array when the cache hasn't been populated yet.
 */
export async function readCachedMyNutritionSessions(): Promise<NutritionMyWorkItem[]> {
  return db.nutritionMySessions.toArray();
}
